import { Footprints, Wind, Bike, Mountain, Waves, Dumbbell, Activity as ActivityGlyph, type LucideIcon } from "lucide-react";
import { useT } from "../lib/i18n";
import { useUnits } from "../lib/useUnits";
import type { Activity } from "@shared/types";

const ACTIVITY_ICON: Record<Activity["type"], LucideIcon> = {
  walk: Footprints, run: Wind, cycle: Bike, hike: Mountain, swim: Waves, strength: Dumbbell, other: ActivityGlyph,
};

interface Props {
  activities: Activity[];
  onAdd: () => void;
  onEdit: (a: Activity) => void;
}

export function ActivityTiles({ activities, onAdd, onEdit }: Props) {
  const t = useT();
  const units = useUnits();
  return (
    // A grid, not a horizontal scroller. This sits on Today, which swipes
    // between tabs: a scroller hands the gesture over at its scroll boundary,
    // so dragging the tiles jumped to the next tab instead (the same bug the
    // date strip had). Fitted columns wrap to a second row instead.
    <div className="grid grid-cols-3 gap-2.5">
      <button
        onClick={onAdd}
        aria-label={t("today.add")}
        className="h-24 glass rounded-2xl flex flex-col items-center justify-center gap-1 press"
      >
        <span aria-hidden="true" className="text-2xl leading-none">＋</span>
        <span className="text-sm text-fg-muted">{t("today.add")}</span>
      </button>
      {activities.filter((a) => !(a.source === "healthkit" && a.kcal_burned === 0)).map((a) => (
        <button
          key={a.id}
          onClick={() => onEdit(a)}
          className="relative h-24 glass rounded-2xl p-3 flex flex-col items-center justify-center gap-0.5 press"
        >
          {a.emoji
            ? <span aria-hidden="true" className="text-[1.375rem] leading-none">{a.emoji}</span>
            : (() => { const TypeIcon = ACTIVITY_ICON[a.type]; return <TypeIcon aria-hidden="true" size={22} className="text-accent-move" />; })()}
          <span className="text-sm font-medium truncate max-w-full">{a.activity_name ?? t(`activity.${a.type}`)}</span>
          <span className="text-sm text-fg-muted tabular-nums">{units.energyStr(a.kcal_burned)}</span>
        </button>
      ))}
    </div>
  );
}
